// import axios from 'axios';
// import { handleApiError } from '../utils/errorHandler.ts';

// const axiosClient = axios.create({
//   baseURL: 'https://api.bsi.theteam.com.np/api/v1',
//   headers: {
//     'Content-Type': 'application/json'
//   }
// });

// // Request interceptor to attach access token
// axiosClient.interceptors.request.use(
//   config => {
//     const token = sessionStorage.getItem('access_token');
//     if (token && config.headers) {
//       config.headers['Authorization'] = `Bearer ${token}`;
//     }
//     return config;
//   },
//   error => Promise.reject(error)
// );

// // Response interceptor to handle 401 and try refresh token
// axiosClient.interceptors.response.use(
//   response => response,
//   async error => {
//     const originalRequest = error.config;

//     // FIXED: Don't handle 401 for login requests - let them fail naturally
//     if (error.response?.status === 401 && !originalRequest._retry) {
//       // FIXED: Skip refresh token logic for login endpoint
//       if (originalRequest.url?.includes('/auth/login')) {
//         console.log('Login failed with 401 - skipping refresh token logic');
//         return Promise.reject(error);
//       }

//       originalRequest._retry = true;

//       const refreshToken = sessionStorage.getItem('refresh_token');
//       if (refreshToken) {
//         try {
//           const response = await axios.post(
//             'https://api.bsi.theteam.com.np/api/v1/auth/refresh',
//             { refresh_token: refreshToken }
//           );

//           const {
//             access_token: newAccessToken,
//             refresh_token: newRefreshToken
//           } = response.data;

//           // Save new tokens
//           sessionStorage.setItem('access_token', newAccessToken);
//           sessionStorage.setItem('refresh_token', newRefreshToken);

//           // Update the Authorization header and retry original request
//           originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
//           return axiosClient(originalRequest);
//         } catch (refreshError) {
//           // Failed to refresh token
//           console.log('Refresh token failed, redirecting to login');
//           handleApiError(refreshError, 'Session expired. Please login again.');
//           sessionStorage.clear();
//           window.location.href = '/login';
//           return Promise.reject(refreshError);
//         }
//       } else {
//         console.log('No refresh token, redirecting to login');
//         sessionStorage.clear();
//         window.location.href = '/login';
//       }
//     }

//     // Auto-display errors (unless silenced or login endpoint)
//     if (
//       !originalRequest.silenceError &&
//       !originalRequest.url?.includes('/auth/login')
//     ) {
//       handleApiError(error);
//     }

//     return Promise.reject(error);
//   }
// );

// export default axiosClient;

import axios from 'axios';
import { handleApiError } from '../utils/errorHandler';
import { isTokenExpired } from '../utils/authSecurity';

const axiosClient = axios.create({
  baseURL: 'https://assetsapi.blueskyint.com.np/api/v1',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to attach access token and validate JWT
axiosClient.interceptors.request.use(
  async config => {
    // Skip token validation for login/refresh endpoints
    if (
      config.url?.includes('/auth/login') ||
      config.url?.includes('/auth/refresh') ||
      config.url?.includes('/auth/forgot-password') ||
      config.url?.includes('/auth/reset-password')
    ) {
      return config;
    }

    // Check both localStorage and sessionStorage for token
    const token =
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('access_token');

    if (token) {
      // Validate JWT expiration before sending request
      if (isTokenExpired(token)) {
        console.log('⚠️ Token expired - attempting refresh before request');

        // Try to refresh token
        const refreshToken =
          localStorage.getItem('refresh_token') ||
          sessionStorage.getItem('refresh_token');

        if (refreshToken) {
          try {
            const response = await axios.post(
              'https://assetsapi.blueskyint.com.np/api/v1/auth/refresh',
              { refresh_token: refreshToken }
            );

            const {
              access_token: newAccessToken,
              refresh_token: newRefreshToken
            } = response.data;

            // Determine which storage to use
            const rememberMe = localStorage.getItem('remember_me') === 'true';
            const storage = rememberMe ? localStorage : sessionStorage;

            // Save new tokens
            storage.setItem('access_token', newAccessToken);
            storage.setItem('refresh_token', newRefreshToken);

            // Use new token for this request
            if (config.headers) {
              config.headers['Authorization'] = `Bearer ${newAccessToken}`;
            }

            console.log('✅ Token refreshed successfully');
          } catch (refreshError: unknown) {
            console.error('❌ Token refresh failed in request interceptor');
            // Check if it's a network error - if so, try with existing token
            const isNetworkError =
              refreshError &&
              typeof refreshError === 'object' &&
              'message' in refreshError &&
              (refreshError as { message: string }).message === 'Network Error';

            if (isNetworkError) {
              console.log(
                'Network error during token refresh - proceeding with existing token'
              );
              // Still attach the existing token - let the server decide
              if (config.headers) {
                config.headers['Authorization'] = `Bearer ${token}`;
              }
              return config;
            }
            // For other errors, let the response interceptor handle it
            throw refreshError;
          }
        } else {
          console.error('❌ No refresh token available');
          // Don't throw - let the request proceed and fail with 401
          // The response interceptor will handle it
          if (config.headers) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        }
      } else {
        // Token is valid, attach it
        if (config.headers) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor to handle 401 and try refresh token
axiosClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Check if this is a network error (no response from server)
    const isNetworkError = !error.response && error.message === 'Network Error';

    // Don't auto-logout on network errors - just show the error
    if (isNetworkError) {
      console.log('Network error detected - not triggering logout');
      if (!originalRequest?.silenceError) {
        handleApiError(error, 'Network error. Please check your connection.');
      }
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't handle 401 for login requests
      if (originalRequest.url?.includes('/auth/login')) {
        console.log('Login failed with 401 - skipping refresh token logic');
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Check both storages for refresh token
      const refreshToken =
        localStorage.getItem('refresh_token') ||
        sessionStorage.getItem('refresh_token');

      if (refreshToken) {
        try {
          const response = await axios.post(
            'https://assetsapi.blueskyint.com.np/api/v1/auth/refresh',
            { refresh_token: refreshToken }
          );

          const {
            access_token: newAccessToken,
            refresh_token: newRefreshToken
          } = response.data;

          // Determine which storage to use based on remember_me flag
          const rememberMe = localStorage.getItem('remember_me') === 'true';
          const storage = rememberMe ? localStorage : sessionStorage;

          // Save new tokens to the same storage
          storage.setItem('access_token', newAccessToken);
          storage.setItem('refresh_token', newRefreshToken);

          // Update the Authorization header and retry original request
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return axiosClient(originalRequest);
        } catch (refreshError: unknown) {
          // Check if refresh failed due to network error
          const isRefreshNetworkError =
            refreshError &&
            typeof refreshError === 'object' &&
            'message' in refreshError &&
            (refreshError as { message: string }).message === 'Network Error';

          if (isRefreshNetworkError) {
            console.log(
              'Token refresh failed due to network error - not logging out'
            );
            return Promise.reject(refreshError);
          }

          // Failed to refresh token due to invalid/expired refresh token - AUTO LOGOUT
          console.log('Refresh token failed, auto-logging out');

          // Clear ALL auth storage
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          localStorage.removeItem('auth');
          localStorage.removeItem('remember_me');

          sessionStorage.clear();

          // Redirect to login page
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token - AUTO LOGOUT
        console.log('No refresh token, auto-logging out');

        // Clear ALL auth storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('auth');
        localStorage.removeItem('remember_me');

        sessionStorage.clear();

        window.location.href = '/login';
      }
    }

    // Auto-display errors (unless silenced or login endpoint)
    if (
      !originalRequest?.silenceError &&
      !originalRequest?.url?.includes('/auth/login')
    ) {
      handleApiError(error);
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
